import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/http";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { HeartPulse, Plus, Trash2, Pencil, X, Eye } from "lucide-react";
import { InstructionsPreview } from "@/components/booking/ClientPreview";

const EMPTY = { title: "", content: "", service_id: "" };

export default function AftercareTemplates() {
  const [tpls, setTpls] = useState([]);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [preview, setPreview] = useState(null);
  const previewOf = (t) => setPreview({ title: t.title, instructions: t.content });

  const load = () => api.get("/aftercare/templates").then(({ data }) => setTpls(data)).catch(() => {});
  useEffect(() => { load(); api.get("/services").then(({ data }) => setServices(data)).catch(() => {}); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
  const openEdit = (t) => { setForm({ title: t.title, content: t.content, service_id: t.service_id || "" }); setEditId(t.id); setShowForm(true); };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Title and instructions are required"); return; }
    try {
      const payload = { ...form, service_id: form.service_id || null };
      if (editId) await api.put(`/aftercare/templates/${editId}`, payload);
      else await api.post("/aftercare/templates", payload);
      toast.success(editId ? "Template updated" : "Template created");
      setShowForm(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this aftercare template? This can't be undone.")) return;
    try { await api.delete(`/aftercare/templates/${id}`); toast.success("Template deleted"); load(); }
    catch { toast.error("Couldn't delete template"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="aftercare-templates-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center"><HeartPulse className="h-6 w-6 text-primary" /></div>
          <div>
            <h1 className="font-heading font-bold text-3xl">Aftercare templates</h1>
            <p className="text-muted-foreground text-sm">Reusable post-op instructions the AI Assistant can send to clients in one tap.</p>
          </div>
        </div>
        <Button onClick={openNew} className="rounded-lg" data-testid="aftercare-tpl-new"><Plus className="h-4 w-4 mr-1" />New template</Button>
      </div>

      {showForm && (
        <Card className="rounded-2xl p-6 space-y-4" data-testid="aftercare-tpl-form">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-lg">{editId ? "Edit template" : "New template"}</h2>
            <button onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Laser Aftercare" className="mt-1 rounded-lg" data-testid="aftercare-tpl-title" />
            </div>
            <div>
              <Label>Linked service (optional)</Label>
              <select value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })} className="mt-1 w-full h-10 rounded-lg border border-border bg-background px-3 text-sm" data-testid="aftercare-tpl-service">
                <option value="">Any / general</option>
                {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label>Instructions</Label>
            <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={7} placeholder={"1. Avoid sun exposure for 48 hours.\n2. Apply the recommended aftercare cream twice daily.\n3. Avoid hot showers, saunas and swimming for 24 hours."} className="mt-1 rounded-lg" data-testid="aftercare-tpl-content" />
          </div>
          <div className="flex gap-2">
            <Button onClick={save} className="rounded-lg" data-testid="aftercare-tpl-save">{editId ? "Save changes" : "Create template"}</Button>
            <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-lg">Cancel</Button>
            <Button variant="ghost" onClick={() => previewOf(form)} className="rounded-lg ml-auto" data-testid="aftercare-tpl-preview"><Eye className="h-4 w-4 mr-1.5" />Preview</Button>
          </div>
        </Card>
      )}

      {!tpls.length ? (
        <Card className="rounded-2xl p-10 text-center text-muted-foreground" data-testid="aftercare-tpl-empty">
          No templates yet. Create one and the Assistant can send it to any client after their treatment.
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {tpls.map((t) => (
            <Card key={t.id} className="rounded-2xl p-5" data-testid={`aftercare-tpl-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold">{t.title}</div>
                  {t.service_name && <div className="text-xs text-primary mt-0.5">{t.service_name}</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => previewOf(t)} className="text-muted-foreground hover:text-foreground p-1" title="Preview" data-testid={`aftercare-tpl-preview-${t.id}`}><Eye className="h-4 w-4" /></button>
                  <button onClick={() => openEdit(t)} className="text-muted-foreground hover:text-foreground p-1" data-testid={`aftercare-tpl-edit-${t.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(t.id)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`aftercare-tpl-del-${t.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="text-sm text-muted-foreground whitespace-pre-wrap mt-3 line-clamp-6">{t.content}</div>
            </Card>
          ))}
        </div>
      )}
      <InstructionsPreview item={preview} kind="aftercare" onClose={() => setPreview(null)} />
    </div>
  );
}
